/**
 * 4×4 matrix utilities for composing / decomposing Euler-angle transforms.
 *
 * Convention: **column-major** storage (same as Three.js / OpenGL / Blender).
 *
 *   elements[ 0.. 3] = column 0  (X basis + w)
 *   elements[ 4.. 7] = column 1  (Y basis + w)
 *   elements[ 8..11] = column 2  (Z basis + w)
 *   elements[12..15] = column 3  (translation + 1)
 *
 * Blender internally uses `mat[col][row]` (see `mul_m4_m4m4` in
 * `source/blender/blenlib/intern/math_matrix.c`).  C lays out the
 * outer dimension first, so `float mat[4][4]` is stored columns-first —
 * already column-major.  The raw 16 floats read from a `.blend` file
 * can therefore be used directly with these utilities, **no transpose**.
 *
 * --- Euler-order convention ---
 *
 * Two conventions ship here because PoseRef has two different rotation
 * sources and they disagree on how to compose an Euler-XYZ triple into a
 * matrix:
 *
 *   1. **`mat4Compose` / `mat4Decompose` / `applyEulerXYZ`** — the
 *      CANONICAL store convention, matching Three.js's default Euler
 *      order `"XYZ"`.  The matrix is `Rx · Ry · Rz`, i.e. applied to a
 *      column vector as `v' = Rx(Ry(Rz · v))` (extrinsic Z-Y-X).  Every
 *      `SceneObject.rotation` value produced by the gizmo, `applyTransform`,
 *      the command pipeline, and the viewport renderer is in this form,
 *      so any utility that interprets those angles as a matrix must use
 *      this convention.
 *
 *   2. **`mat4ComposeBlender` / `mat4DecomposeBlender` /
 *      `applyEulerXYZBlender`** — Blender's native XYZ Euler convention.
 *      The matrix is `Rz · Ry · Rx`, i.e. `v' = Rz(Ry(Rx · v))` (intrinsic
 *      X-Y-Z, which is what Blender's `eul_to_mat3` produces — see
 *      `source/blender/blenlib/intern/math_rotation.c`).  Only the .blend
 *      importer uses this, at the raw-file boundary, to read Euler angles
 *      as they appear in the .blend binary before converting to the
 *      canonical store form.
 *
 * These two conventions agree for any single-axis rotation and diverge
 * for multi-axis rotations.  Mixing them caused a hard-won bug where a
 * tilted cylinder-cutter Boolean'd against a plate appeared to rotate
 * "faster" than the cutter object around its Z axis — the viewport
 * rendered the cutter under convention (1) while the modifier pipeline
 * resolved it under (2).  The Z-symmetry of the cylinder absorbed the
 * viewport-side rotation but not the pipeline-side, producing the
 * observed rate mismatch that vanished only when Ctrl+A was used to bake
 * the transform and zero out the rotation.  Don't re-introduce (2) into
 * anything downstream of the store — if you need to interpret a
 * `SceneObject.rotation`, use `mat4Compose`.
 */

// ---------- types ----------------------------------------------------------

export type Mat4 = number[];

// ---------- constructors ---------------------------------------------------

export function mat4Identity(): Mat4 {
  // prettier-ignore
  return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}

/**
 * Compose a column-major TRS matrix from position, Euler-XYZ rotation, and
 * scale, using the CANONICAL store / Three.js convention.
 *
 * Rotation matrix:  R = Rx · Ry · Rz   (Three.js `Euler` order `"XYZ"`,
 * i.e. the same matrix `new THREE.Matrix4().compose()` produces from an
 * `Euler(ex, ey, ez)` with the default order — applied to a column
 * vector, the Z rotation is done first, then Y, then X).
 *
 * Use this for any matrix built from a `SceneObject.rotation` (the gizmo,
 * the command pipeline, `applyTransform`, and every viewport renderer
 * all write into that field under this convention).  For the Blender
 * native convention — only needed at the .blend raw-import boundary —
 * use `mat4ComposeBlender`.
 */
export function mat4Compose(
  pos: readonly [number, number, number],
  euler: readonly [number, number, number],
  scale: readonly [number, number, number],
): Mat4 {
  const [ex, ey, ez] = euler;
  const cx = Math.cos(ex),
    sx = Math.sin(ex);
  const cy = Math.cos(ey),
    sy = Math.sin(ey);
  const cz = Math.cos(ez),
    sz = Math.sin(ez);
  const [scX, scY, scZ] = scale;

  // R = Rx · Ry · Rz, column-major.  Each column is scaled independently
  // (scX for col 0 / X basis, scY for col 1, scZ for col 2) so non-uniform
  // scales survive the compose → decompose round trip.
  //
  // Matches Three.js `Matrix4.makeRotationFromEuler` with order `"XYZ"`:
  //   te[0]  = cy*cz         te[4]  = -cy*sz        te[8]  =  sy
  //   te[1]  = cx*sz+sx*sy*cz te[5]  = cx*cz-sx*sy*sz te[9]  = -sx*cy
  //   te[2]  = sx*sz-cx*sy*cz te[6]  = sx*cz+cx*sy*sz te[10] =  cx*cy
  // prettier-ignore
  return [
    /* col 0 */ cy * cz * scX,
    (cx * sz + sx * sy * cz) * scX,
    (sx * sz - cx * sy * cz) * scX,
    0,
    /* col 1 */ -cy * sz * scY,
    (cx * cz - sx * sy * sz) * scY,
    (sx * cz + cx * sy * sz) * scY,
    0,
    /* col 2 */ sy * scZ,
    -sx * cy * scZ,
    cx * cy * scZ,
    0,
    /* col 3 */ pos[0],
    pos[1],
    pos[2],
    1,
  ];
}

/**
 * Blender-native variant of `mat4Compose`.  Rotation matrix is
 * `Rz · Ry · Rx`, matching Blender's `eul_to_mat3` output for default
 * XYZ rotation mode.  Used ONLY by the .blend importer while it is
 * working with raw Euler angles read directly from the file — anything
 * downstream of `bakeWorldTransforms` should already be in the canonical
 * (Three.js) form and should use `mat4Compose`.
 */
export function mat4ComposeBlender(
  pos: readonly [number, number, number],
  euler: readonly [number, number, number],
  scale: readonly [number, number, number],
): Mat4 {
  const [ex, ey, ez] = euler;
  const cx = Math.cos(ex),
    sx = Math.sin(ex);
  const cy = Math.cos(ey),
    sy = Math.sin(ey);
  const cz = Math.cos(ez),
    sz = Math.sin(ez);
  const [scX, scY, scZ] = scale;

  // R = Rz · Ry · Rx   (each column scaled by scX / scY / scZ)
  // prettier-ignore
  return [
    /* col 0 */ cy * cz * scX,
    cy * sz * scX,
    -sy * scX,
    0,
    /* col 1 */ (cz * sx * sy - cx * sz) * scY,
    (cx * cz + sx * sy * sz) * scY,
    cy * sx * scY,
    0,
    /* col 2 */ (cx * cz * sy + sx * sz) * scZ,
    (-cz * sx + cx * sy * sz) * scZ,
    cx * cy * scZ,
    0,
    /* col 3 */ pos[0],
    pos[1],
    pos[2],
    1,
  ];
}

// ---------- operations -----------------------------------------------------

/** Multiply two column-major 4×4 matrices:  result = a · b */
export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      out[col * 4 + row] =
        a[0 * 4 + row] * b[col * 4 + 0] +
        a[1 * 4 + row] * b[col * 4 + 1] +
        a[2 * 4 + row] * b[col * 4 + 2] +
        a[3 * 4 + row] * b[col * 4 + 3];
    }
  }
  return out;
}

/** Return `true` when every element of `m` is zero. */
function isZeroMatrix(m: readonly number[]): boolean {
  for (let i = 0; i < m.length; i++) if (m[i] !== 0) return false;
  return true;
}

/**
 * Convert a Blender `parentinv` raw float[4][4] to a column-major Mat4.
 *
 * Falls back to the identity matrix when the data is missing (length < 16)
 * or all-zero (which Blender writes for unparented objects in some versions).
 *
 * No transpose is needed: Blender's `float mat[4][4]` uses `mat[col][row]`,
 * so reading 16 consecutive floats from the .blend file already yields
 * column-major data matching our convention.
 */
export function parentInverseToMat4(raw: readonly number[]): Mat4 {
  if (raw.length < 16 || isZeroMatrix(raw)) return mat4Identity();
  return Array.from(raw);
}

// ---------- inverse --------------------------------------------------------

/**
 * Invert a column-major 4×4 matrix.  Returns the identity if the matrix is
 * singular (determinant ≈ 0).
 */
export function mat4Inverse(m: Mat4): Mat4 {
  const a00 = m[0],
    a01 = m[1],
    a02 = m[2],
    a03 = m[3];
  const a10 = m[4],
    a11 = m[5],
    a12 = m[6],
    a13 = m[7];
  const a20 = m[8],
    a21 = m[9],
    a22 = m[10],
    a23 = m[11];
  const a30 = m[12],
    a31 = m[13],
    a32 = m[14],
    a33 = m[15];

  const b00 = a00 * a11 - a01 * a10;
  const b01 = a00 * a12 - a02 * a10;
  const b02 = a00 * a13 - a03 * a10;
  const b03 = a01 * a12 - a02 * a11;
  const b04 = a01 * a13 - a03 * a11;
  const b05 = a02 * a13 - a03 * a12;
  const b06 = a20 * a31 - a21 * a30;
  const b07 = a20 * a32 - a22 * a30;
  const b08 = a20 * a33 - a23 * a30;
  const b09 = a21 * a32 - a22 * a31;
  const b10 = a21 * a33 - a23 * a31;
  const b11 = a22 * a33 - a23 * a32;

  const det =
    b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-12) return mat4Identity();

  const invDet = 1 / det;
  return [
    (a11 * b11 - a12 * b10 + a13 * b09) * invDet,
    (-a01 * b11 + a02 * b10 - a03 * b09) * invDet,
    (a31 * b05 - a32 * b04 + a33 * b03) * invDet,
    (-a21 * b05 + a22 * b04 - a23 * b03) * invDet,
    (-a10 * b11 + a12 * b08 - a13 * b07) * invDet,
    (a00 * b11 - a02 * b08 + a03 * b07) * invDet,
    (-a30 * b05 + a32 * b02 - a33 * b01) * invDet,
    (a20 * b05 - a22 * b02 + a23 * b01) * invDet,
    (a10 * b10 - a11 * b08 + a13 * b06) * invDet,
    (-a00 * b10 + a01 * b08 - a03 * b06) * invDet,
    (a30 * b04 - a31 * b02 + a33 * b00) * invDet,
    (-a20 * b04 + a21 * b02 - a23 * b00) * invDet,
    (-a10 * b09 + a11 * b07 - a12 * b06) * invDet,
    (a00 * b09 - a01 * b07 + a02 * b06) * invDet,
    (-a30 * b03 + a31 * b01 - a32 * b00) * invDet,
    (a20 * b03 - a21 * b01 + a22 * b00) * invDet,
  ];
}

// ---------- decomposition --------------------------------------------------

/**
 * Shared scale + sign + normalised-rotation extraction used by both
 * decompose variants.  Returns the scale vector and the 9 normalised
 * rotation-matrix elements `rIJ = row I col J` in the `R = T^-1 · M · S^-1`
 * sense (i.e. with translation stripped and scale divided out).
 */
function extractScaleAndRotation(m: Mat4): {
  scale: [number, number, number];
  r00: number;
  r10: number;
  r20: number;
  r01: number;
  r11: number;
  r21: number;
  r02: number;
  r12: number;
  r22: number;
} {
  // Column lengths = scale magnitudes
  let sx = Math.sqrt(m[0] * m[0] + m[1] * m[1] + m[2] * m[2]);
  const sy = Math.sqrt(m[4] * m[4] + m[5] * m[5] + m[6] * m[6]);
  const sz = Math.sqrt(m[8] * m[8] + m[9] * m[9] + m[10] * m[10]);

  // Determinant sign → flip sx for reflections (matches Three.js
  // `Matrix4.decompose`, which also flips the X scale when det < 0).
  const det =
    m[0] * (m[5] * m[10] - m[6] * m[9]) -
    m[4] * (m[1] * m[10] - m[2] * m[9]) +
    m[8] * (m[1] * m[6] - m[2] * m[5]);
  if (det < 0) sx = -sx;

  const invSx = sx !== 0 ? 1 / sx : 0;
  const invSy = sy !== 0 ? 1 / sy : 0;
  const invSz = sz !== 0 ? 1 / sz : 0;

  return {
    scale: [sx, sy, sz],
    r00: m[0] * invSx,
    r10: m[1] * invSx,
    r20: m[2] * invSx,
    r01: m[4] * invSy,
    r11: m[5] * invSy,
    r21: m[6] * invSy,
    r02: m[8] * invSz,
    r12: m[9] * invSz,
    r22: m[10] * invSz,
  };
}

/**
 * Decompose a column-major TRS matrix into position, Euler-XYZ rotation, and
 * scale, in the CANONICAL store / Three.js convention.
 *
 * Extracts the Euler triple such that `mat4Compose(position, rotation, scale)`
 * round-trips the input matrix (modulo floating-point error and the usual
 * ambiguity around gimbal lock).  The formula below is exactly Three.js's
 * `Euler.setFromRotationMatrix` with order `"XYZ"`, so feeding the returned
 * rotation into `new THREE.Euler(rx, ry, rz)` also produces the same
 * orientation.
 */
export function mat4Decompose(m: Mat4): {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
} {
  const position: [number, number, number] = [m[12], m[13], m[14]];
  const { scale, r00, r01, r02, r11, r12, r21, r22 } =
    extractScaleAndRotation(m);

  // Euler-XYZ extraction from rotation matrix  R = Rx · Ry · Rz.
  //   r02 =  sin(y)                →  y = asin(r02)
  //   r12 = -sin(x)*cos(y)
  //   r22 =  cos(x)*cos(y)         →  x = atan2(-r12, r22)     (non-gimbal)
  //   r00 =  cos(y)*cos(z)
  //   r01 = -cos(y)*sin(z)         →  z = atan2(-r01, r00)     (non-gimbal)
  // At the gimbal (|r02| → 1), cos(y) collapses to 0 and the standard
  // fallback pins z = 0 and solves x from r11 / r21.
  let ex: number, ey: number, ez: number;
  const clamped = Math.max(-1, Math.min(1, r02));

  if (Math.abs(clamped) < 1 - 1e-7) {
    ey = Math.asin(clamped);
    ex = Math.atan2(-r12, r22);
    ez = Math.atan2(-r01, r00);
  } else {
    // Gimbal lock — y = ±π/2, and only (x + z) is determined.  Pin z to 0.
    ey = clamped > 0 ? Math.PI / 2 : -Math.PI / 2;
    ex = Math.atan2(r21, r11);
    ez = 0;
  }

  return { position, rotation: [ex, ey, ez], scale };
}

/**
 * Blender-native variant of `mat4Decompose` — extracts an XYZ Euler
 * under the `R = Rz · Ry · Rx` convention Blender uses internally.
 * Only needed inside the .blend importer, for the (rare) path that
 * composes a world matrix from raw Blender Euler values and then needs
 * to round-trip them.  Everything at or downstream of the store should
 * use `mat4Decompose`.
 */
export function mat4DecomposeBlender(m: Mat4): {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
} {
  const position: [number, number, number] = [m[12], m[13], m[14]];
  const { scale, r00, r10, r20, r11, r12, r21, r22 } =
    extractScaleAndRotation(m);

  // Euler-XYZ extraction from rotation matrix  R = Rz · Ry · Rx.
  let ex: number, ey: number, ez: number;
  const clamped = Math.max(-1, Math.min(1, r20));

  if (Math.abs(clamped) < 1 - 1e-7) {
    ey = Math.asin(-clamped);
    ex = Math.atan2(r21, r22);
    ez = Math.atan2(r10, r00);
  } else {
    // Gimbal lock
    ey = clamped < 0 ? Math.PI / 2 : -Math.PI / 2;
    ex = Math.atan2(-r12, r11);
    ez = 0;
  }

  return { position, rotation: [ex, ey, ez], scale };
}

// ---------- vector helpers -------------------------------------------------

/**
 * Apply an Euler-XYZ rotation to a 3-D vector, CANONICAL / Three.js
 * convention.  Equivalent to `v' = (Rx · Ry · Rz) · v` — mathematically
 * identical to `new THREE.Vector3(...v).applyEuler(new THREE.Euler(ex, ey, ez))`
 * with the default `"XYZ"` order.
 *
 * The rotations are composed into the vector from right to left: first
 * `Rz` hits the input, then `Ry`, then `Rx`.  For the Blender native
 * variant (`Rz · Ry · Rx`), use `applyEulerXYZBlender`.
 */
export function applyEulerXYZ(
  euler: readonly [number, number, number],
  v: readonly [number, number, number],
): [number, number, number] {
  const [ex, ey, ez] = euler;
  const cx = Math.cos(ex),
    sx = Math.sin(ex);
  const cy = Math.cos(ey),
    sy = Math.sin(ey);
  const cz = Math.cos(ez),
    sz = Math.sin(ez);

  let [x, y, z] = v;

  // Rz  (applied to the raw input first because the matrix is Rx·Ry·Rz
  //      and matrix multiplication is read right-to-left on column vectors)
  let tmp = cz * x - sz * y;
  y = sz * x + cz * y;
  x = tmp;

  // Ry
  tmp = cy * x + sy * z;
  z = -sy * x + cy * z;
  x = tmp;

  // Rx
  tmp = cx * y - sx * z;
  z = sx * y + cx * z;
  y = tmp;

  return [x, y, z];
}

/**
 * Blender-native variant of `applyEulerXYZ`.  Equivalent to
 * `v' = (Rz · Ry · Rx) · v`, matching Blender's `eul_to_mat3`.  Used
 * by the .blend importer for one specific path (re-centering mesh
 * geometries) where the rotation value is still in raw Blender form —
 * everything downstream of `bakeWorldTransforms` should already be in
 * the canonical store convention and should use `applyEulerXYZ`.
 */
export function applyEulerXYZBlender(
  euler: readonly [number, number, number],
  v: readonly [number, number, number],
): [number, number, number] {
  const [ex, ey, ez] = euler;
  const cx = Math.cos(ex),
    sx = Math.sin(ex);
  const cy = Math.cos(ey),
    sy = Math.sin(ey);
  const cz = Math.cos(ez),
    sz = Math.sin(ez);

  let [x, y, z] = v;

  // Rx
  let tmp = cx * y - sx * z;
  z = sx * y + cx * z;
  y = tmp;

  // Ry
  tmp = cy * x + sy * z;
  z = -sy * x + cy * z;
  x = tmp;

  // Rz
  tmp = cz * x - sz * y;
  y = sz * x + cz * y;
  x = tmp;

  return [x, y, z];
}
